import React, { useMemo } from 'react';
import { Button, Checkbox, FormControl, FormControlLabel, Typography, useTheme } from '@mui/material';
import CreateActivityInstructions from './create-activity-instructions/create-activity-instructions';
import CreateActivityResources from './create-activity-resources/create-activity-resources';
import './create-activity-instruction-resources.scss';
import { useAppSelector } from 'app/config/store';
import LoaderIcon from 'app/components/common/loader-icon/loader-icon';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const CreateActivityInstructionResources = ({ isError, formik, isButtonDisabled, onCancel }: any) => {
  const isLoading = useAppSelector(state => state.homeActivity.loading);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleCancel = () => {
    formik.setFieldValue('instructions', []);
    formik.resetForm();
  };

  const navigate = useNavigate();

  // Time comparison helper
  const isSameTime =
    dayjs(formik.values.activityStartTime).format('YYYY-MM-DD HH:mm') ===
    dayjs(formik.values.activityEndTime).format('YYYY-MM-DD HH:mm');

  // CRITICAL FIX: Real-time URL validation (doesn't depend on blur/focus)
  const hasInvalidOnlineUrl = useMemo(() => {
    // Skip validation if not an online event
    if (!formik.values.online) return false;

    // Skip validation for lost pet category
    if (formik.values.subCategoryID === 72) return false;

    const url = formik.values.onlineUrl;

    // Check if URL is empty
    if (!url || url.trim() === '') return true;

    // Check if URL starts with http:// or https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) return true;

    // Check if URL is from supported domains
    const supportedDomains = [
      'teams.microsoft.com',
      'meet.google.com',
      'zoom.us',
      'webex.com',
      'gotomeeting.com',
      'gotomeet.me',
      'meet.goto.com',
      'meeting.zoho.com',
      'zoho.com',
      'meet.jit.si',
      'whereby.com',
      'bluejeans.com',
      'adobeconnect.com',
      'lifesize.com',
      'call.lifesize.com',
      'slack.com',
      'bigbluebutton.org',
      'chime.aws',
      'ringcentral.com',
      'skype.com',
      'discord.com',
      'voovmeeting.com',
      'meeting.tencent.com',
      'hopin.com',
      '8x8.vc',
      '8x8.com',
      'starleaf.com',
      'freeconference.com',
      'meetfox.com',
      'clickmeeting.com',
      'teamviewer.com',
    ];

    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      const isValidDomain = supportedDomains.some(
        domain => hostname === domain || hostname.endsWith('.' + domain)
      );

      return !isValidDomain; // Return true if invalid
    } catch (error) {
      return true; // Invalid URL format
    }
  }, [formik.values.online, formik.values.onlineUrl, formik.values.subCategoryID]);

  // Combined validation
  const isPostButtonDisabled =
    !formik.dirty ||
    !formik.isValid ||
    isSameTime ||
    hasInvalidOnlineUrl;

  return (
    <div className="d-flex flex-column align-items-center justify-content-center " style={{ rowGap: '10px' }}>
      <div className="col-12 mt-sm-2 mt-2 mt-md-0">
        <CreateActivityInstructions formik={formik} />
      </div>
      {formik.values.subCategoryID !== 72 && (
        <div className="col-12">
          <CreateActivityResources formik={formik} />
        </div>
      )}
      <div className="fixed-button1 text-center">
        <div>
          {formik.values.categoryID !== 19 &&
            formik.values.subCategoryID !== 72 &&
            (formik.values.activityFrequencyId === 1 || formik.values.activityFrequencyId === '') && (
              <FormControl component="fieldset">
                <FormControlLabel
                  sx={{ color: 'secondary.main', fontSize: '14px' }}
                  value="end"
                  control={
                    <Checkbox
                      name="urgentValue"
                      checked={formik.values.urgentValue === true}
                      onChange={event => {
                        if (event.target.checked) {
                          formik.values.attendees = false;
                          formik.values.participantOption = 'VOLUNTEER_ONLY';
                          formik.values.attendeeCapacity = '';
                          formik.values.volunteer = true;
                        }
                        formik.setFieldValue('urgentValue', event.target.checked);
                      }}
                      disableRipple
                      sx={{
                        color: 'transparent',
                        '&.Mui-checked': { color: 'primary.light' },
                        '&.MuiCheckbox-root': { paddingBottom: '0px', paddingTop: '0px' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 23,
                          borderRadius: '25%',
                          border: '1px solid #BFBFBF',
                          marginLeft: '5px',
                          padding: '3px',
                        },
                      }}
                    />
                  }
                  label={<Typography sx={{ color: 'secondary.main', fontSize: '12px' }}>Mark as urgent</Typography>}
                  labelPlacement="end"
                />
              </FormControl>
            )}
        </div>

        <div>
          <Button
            className="m-1"
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: isPostButtonDisabled ? 'secondary.contrastText' : 'primary.main',
              textTransform: 'none',
              borderRadius: '15px',
              width: '200px',
              color: isPostButtonDisabled ? 'secondary.dark' : 'common.black',
              height: '40px',
              '&:hover': {
                backgroundColor: isPostButtonDisabled ? 'secondary.contrastText' : 'primary.main',
                color: isPostButtonDisabled ? 'secondary.dark' : 'common.black',
              },
            }}
            endIcon={isLoading && formik.isValid ? <LoaderIcon /> : null}
            disabled={
              formik.isSubmitting ||
              isButtonDisabled ||
              isError ||
              isPostButtonDisabled
            }
          >
            Post now
          </Button>

          <Button
            className="m-1"
            type="button"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
              textTransform: 'none',
              borderRadius: '15px',
              width: '200px',
              color: isDarkMode ? 'black' : 'white',
              '&:hover': {
                backgroundColor: isDarkMode ? '#FFCE00' : '#674BFF',
              },
            }}
            endIcon={isLoading && formik.isValid ? <LoaderIcon /> : null}
            disabled={formik.isSubmitting || isButtonDisabled || isError || isSameTime}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateActivityInstructionResources;